/**
 * Ids that pair a label with its control on both sides of hydration: its own
 * module, so a hydration or a server render takes the counter and nothing
 * else of the accessibility helpers.
 */

/** Where ids are drawn from while a server render or a hydration walks a page in document order. */
interface IdScope {
  next: number;
}

let scope: IdScope | null = null;
let browserNext = 0;

/**
 * Runs `fn` drawing ids from `into`: the server renderer does this for a
 * render, and a hydration for its walk, so both number the same page alike.
 */
export function withIdScope<T>(into: IdScope, fn: () => T): T {
  const previous = scope;
  scope = into;
  try {
    return fn();
  } finally {
    scope = previous;
  }
}

/** A fresh counter for one render or one hydration. */
export function idScope(): IdScope {
  return { next: 0 };
}

/**
 * An id, fixed the first time it is read.
 *
 * Read it wherever it is needed — `for` on a label and `id` on its input, in
 * a template — and each read gives the same string. It is numbered where it
 * is first written: a server render and the hydration of its page reach it in
 * the same place in document order, so they agree, rows of a list included.
 * Content built only in the browser takes ids of its own (`field-b3`), which
 * never collide with the server's.
 */
export interface Id {
  (): string;
  toString(): string;
}

export function id(prefix = 'c'): Id {
  // Numbered once per scope: a server render that waits for data and renders
  // again numbers its kept pass afresh, in that pass's document order.
  const byScope = new WeakMap<IdScope, string>();
  let last: string | null = null;
  const read = (): string => {
    if (scope) {
      let value = byScope.get(scope);
      if (value === undefined) {
        value = `${prefix}-${scope.next++}`;
        byScope.set(scope, value);
      }
      last = value;
      return value;
    }
    // Outside a render or a hydration — a binding running again later — the
    // id is what it was last given; content built only in the browser gets one of its own.
    return (last ??= `${prefix}-b${browserNext++}`);
  };
  return Object.assign(read, { toString: read });
}
